import InfiniteScroll from "react-infinite-scroll-component";
import styled from "@emotion/styled";
import { Skeleton } from "antd5";

import Button from "components/ui/button/Button";
import { appRoutes } from "lib/appRoutes";
import { routes } from "lib/routes";
import { EventData } from "lib/tracking";
import { pluralise } from "lib/utils";
import CentredSpinner from "../../../lib/core_components/CentredSpinner";
import {
  EntityActionParams,
  useLeadEntityActions,
} from "../../../lib/hooks/api/leads/useLeadEntityActions";
import { LeadFromSearch } from "../../../lib/hooks/api/leads/useLeads";
import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { smoothTransition } from "../../../styles/animations";
import { Flex, Text } from "../../../styles/utility-components";
import { ToastAction } from "../../entities/ToastAction";
import { AllEntities } from "../../entities/utils";
import EmptyState from "../../ui/empty_state/EmptyState";
import { MAX_MATCHING_BUYERS } from "./SaveToLead";

export const LEAD_LIST_CONTAINER_ID = "save-to-lead-list";

type LeadWithSavedStatus = LeadFromSearch & { isSaved: boolean };

type Props = {
  isLoading: boolean;
  isError: boolean;
  leads: LeadWithSavedStatus[];
  total: number;
  /** Single-type selection. Provide this OR `entities` (mixed). */
  entityIds?: string[];
  entityType?: AllEntities;
  /** Mixed-source selection; takes precedence over the entityType/entityIds pair. */
  entities?: EntityActionParams[];
  buyerIds: string[] | undefined;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  buyerMatchOnly?: boolean;
  onLeadSaved?: (leadId: string) => void;
};
export function SaveToLeadList({
  isLoading,
  isError,
  leads,
  total,
  buyerIds,
  entityIds,
  entityType,
  entities,
  fetchNextPage,
  hasNextPage,
  buyerMatchOnly,
  onLeadSaved,
}: Props) {
  if (isLoading) {
    return <Skeleton active />;
  }

  if (isError) {
    return (
      <EmptyState
        type="loadError"
        heading="Sorry something has gone wrong loading leads"
        description="If this error persists please contact support"
        width="100%"
        size="large"
      />
    );
  }

  if (leads.length === 0) {
    return (
      <EmptyState
        heading="You haven't created any leads yet"
        description="Create leads once you identify leads from Stotles, which can then be tracked and progressed to our public sector pipeline."
        width="100%"
        size="large"
      />
    );
  }

  if (buyerMatchOnly) {
    return (
      <LeadsListContainer
        dataLength={leads.length}
        next={fetchNextPage}
        hasMore={hasNextPage || false}
        loader={<CentredSpinner />}
        scrollableTarget={LEAD_LIST_CONTAINER_ID}
      >
        <Flex column gap={8}>
          {leads.map((lead) => (
            <LeadListItem
              key={lead.id}
              lead={lead}
              entityIds={entityIds}
              entityType={entityType}
              entities={entities}
              trackingData={{
                "Lead name": lead.name,
                "Lead stage": lead.stage.name,
                "Lead value": lead.value,
              }}
              onLeadSaved={onLeadSaved}
            />
          ))}
        </Flex>
      </LeadsListContainer>
    );
  }

  const leadsWithBuyer = leads.filter((opp) =>
    opp.buyers.some((buyer) => buyerIds?.includes(buyer.id)),
  );
  const leadsWithoutBuyer = leads.filter(
    (opp) => !leadsWithBuyer.some((buyerOpp) => buyerOpp.id === opp.id),
  );

  const renderMatchingBuyerText = () => {
    if (leadsWithBuyer.length >= MAX_MATCHING_BUYERS && hasNextPage) {
      return `${leadsWithBuyer.length}+ Leads identified with same buyer`;
    }

    return `${pluralise(leadsWithBuyer.length, "Lead")} identified with same buyer`;
  };

  return (
    <LeadsListContainer
      dataLength={leads.length}
      next={fetchNextPage}
      hasMore={hasNextPage || false}
      loader={<CentredSpinner />}
      scrollableTarget={LEAD_LIST_CONTAINER_ID}
    >
      {leadsWithBuyer.length > 0 && (
        <MatchingBuyerLeadsSection>
          <Text color="sysPrimaryPressed">{renderMatchingBuyerText()}</Text>
          {leadsWithBuyer.map((lead) => (
            <LeadListItem
              key={lead.id}
              lead={lead}
              entityIds={entityIds}
              entityType={entityType}
              entities={entities}
              trackingData={{
                "Lead name": lead.name,
                "Lead stage": lead.stage.name,
                "Lead value": lead.value,
              }}
              onLeadSaved={onLeadSaved}
            />
          ))}
        </MatchingBuyerLeadsSection>
      )}

      {leadsWithoutBuyer.length > 0 && (
        <Flex column gap={8}>
          <Text color="sysTextSecondary">
            {total - leadsWithBuyer.length} {leadsWithBuyer.length > 0 && "Other "}
            Leads
          </Text>
          {leadsWithoutBuyer.map((lead) => (
            <LeadListItem
              key={lead.id}
              lead={lead}
              entityIds={entityIds}
              entityType={entityType}
              entities={entities}
              trackingData={{
                "Lead name": lead.name,
                "Lead stage": lead.stage.name,
                "Lead value": lead.value,
              }}
              onLeadSaved={onLeadSaved}
            />
          ))}
        </Flex>
      )}
    </LeadsListContainer>
  );
}

function LeadListItem({
  lead,
  entityIds,
  entityType,
  entities,
  trackingData,
  onLeadSaved,
}: {
  lead: LeadWithSavedStatus;
  entityIds?: string[];
  entityType?: AllEntities;
  entities?: EntityActionParams[];
  trackingData: EventData;
  onLeadSaved?: (leadId: string) => void;
}) {
  const { removeLeadEntities, addLeadEntities, isLoading } = useLeadEntityActions({
    leadId: lead.id,
    trackingData,
    onRemoveEntityAction: <ToastAction objectType="lead" url={`${routes.leads}/${lead.id}`} />,
    onAddEntityAction: <ToastAction objectType="lead" url={`${routes.leads}/${lead.id}`} />,
    onAddSuccess: () => onLeadSaved?.(lead.id),
  });

  const allBuyers = [...lead.buyers, ...lead.customBuyers];

  const onToggleSave = () => {
    // Mixed selection takes precedence; otherwise map the single-type ids to the action shape.
    const actionEntities: EntityActionParams[] =
      entities ??
      (entityType ? (entityIds ?? []).map((entityId) => ({ entityId, entityType })) : []);
    if (lead.isSaved) {
      removeLeadEntities(actionEntities);
    } else {
      // If not saved, add all entities - our backend now handles duplicates
      addLeadEntities(actionEntities);
    }
  };

  return (
    <LeadItem key={lead.id} aria-label={lead.name}>
      <ContentFlex>
        <Flex column minWidth={0}>
          <Text ellipsis>{lead.name}</Text>
          <Text description ellipsis color="sysTextSecondary">
            {lead.description}
          </Text>
          {allBuyers.length > 0 && (
            <Text key={allBuyers[0].id} description ellipsis color="sysTextSecondary">
              {allBuyers[0].name}{" "}
              {allBuyers.length > 1 &&
                `& ${pluralise(allBuyers.length - 1, "buyer", undefined, "more")}`}
            </Text>
          )}
        </Flex>
        {lead.isSaved && <BookmarkIcon icon="bookmark" color="sysPrimaryDefault" size={12} />}
      </ContentFlex>
      <ActionsContainer>
        <Button
          icon="bookmark"
          iconProps={{
            size: 16,
            fill: lead.isSaved ? "currentColor" : "none",
            color: "currentColor",
          }}
          aria-label={lead.isSaved ? `Remove from lead ${lead.name}` : `Add to lead ${lead.name}`}
          onClick={onToggleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          {lead.isSaved ? "Unsave" : "Save"}
        </Button>
        <Button as="a" buttonType="secondary" {...appRoutes.leadDetail(lead.id)}>
          View
        </Button>
      </ActionsContainer>
    </LeadItem>
  );
}

const ActionsContainer = styled.div({
  display: "none",
  gap: 4,
});

const BookmarkIcon = styled(UIcon)({
  display: "flex",
  alignItems: "center",
  marginLeft: "8px",
});

const LeadsListContainer = styled(InfiniteScroll)({
  display: "flex",
  flexDirection: "column",
  gap: 32,
  height: "inherit",
  overflow: "auto",
});

const MatchingBuyerLeadsSection = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 8,
  padding: 16,
  backgroundColor: theme.colors.sysPrimarySubtle,
  borderRadius: 4,
}));

const LeadItem = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  boxSizing: "content-box",
  gap: 16,
  height: 46,
  border: "1px solid",
  borderRadius: 4,
  padding: "12px 16px",
  backgroundColor: theme.colors.sysTextWhite,
  borderColor: theme.colors.sysBorderDecorative,
  alignItems: "center",
  transition: smoothTransition,

  "&:hover": {
    backgroundColor: theme.colors.sysPrimarySubtle,
    borderColor: theme.colors.sysPrimaryDefault,
    [`${ActionsContainer}`]: {
      display: "flex",
    },
    [`${BookmarkIcon}`]: {
      display: "none",
    },
  },
}));

const ContentFlex = styled(Flex)({
  width: "100%",
  justifyContent: "space-between",
  alignItems: "center",
  overflow: "hidden",
  flex: 1,
});
